import { supabase } from './authService';

/**
 * Fetch a document by ID without requiring authentication
 * This is used for public access to documents for signing
 * @param {string} documentId - The document ID to fetch
 * @returns {Promise<Object>} - The document data
 */
export const fetchPublicDocument = async (documentId) => {
  try {
    if (!documentId) {
      throw new Error('Document ID is required');
    }
    
    // Fetch the document from Supabase
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('id', documentId)
      .single();
    
    if (error) {
      console.error('Error fetching public document:', error);
      throw error;
    }
    
    if (!data) {
      throw new Error('Document not found');
    }
    
    return data;
  } catch (err) {
    console.error('Error in fetchPublicDocument:', err);
    throw err;
  }
};

/**
 * Mark a document as signed without requiring authentication
 * This is used for public access to sign documents
 * @param {string} documentId - The document ID to update
 * @param {Object} signedData - The signature data
 * @returns {Promise<Object>} - The updated document
 */
export const markPublicDocumentAsSigned = async (documentId, signedData) => {
  try {
    if (!documentId) {
      throw new Error('Document ID is required');
    }
    
    if (!signedData) {
      throw new Error('Signature data is required');
    }
    
    // Update the document in Supabase
    const { data, error } = await supabase
      .from('documents')
      .update({
        status: 'signed',
        signed_at: new Date().toISOString(),
        signed_data: signedData
      })
      .eq('id', documentId)
      .select()
      .single();
    
    if (error) {
      console.error('Error marking public document as signed:', error);
      throw error;
    }
    
    return data;
  } catch (err) {
    console.error('Error in markPublicDocumentAsSigned:', err);
    throw err;
  }
};

/**
 * Fetch a document for viewing without requiring authentication
 * This is used for public access to view signed documents
 * @param {string} documentId - The document ID to fetch
 * @returns {Promise<Object>} - The document data
 */
export const fetchPublicDocumentForViewing = async (documentId) => {
  try {
    if (!documentId) {
      throw new Error('Document ID is required');
    }
    
    // Fetch the document from Supabase
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('id', documentId)
      .eq('status', 'signed') // Only allow viewing signed documents
      .single();
    
    if (error) {
      console.error('Error fetching public document for viewing:', error);
      throw error;
    }
    
    if (!data) {
      throw new Error('Document not found or not signed');
    }
    
    return data;
  } catch (err) {
    console.error('Error in fetchPublicDocumentForViewing:', err);
    throw err;
  }
};

export default {
  fetchPublicDocument,
  markPublicDocumentAsSigned,
  fetchPublicDocumentForViewing
};
